import { useLazyQuery } from '@apollo/client';
import type { Cell, Renderable, Row, SortingState, Table } from '@tanstack/react-table';
import { flexRender, getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import {
  DesoBalancesDocument,
  DesoBalancesOrderBy,
  HoldersByPublicKeyDocument,
  HoldersByPublicKeyQuery,
  TokenBalanceSummariesOrderBy,
  TokenBalanceSummary,
  TotalSupplyDocument,
} from 'graphql/codegen/graphql';
import { CSSProperties, useContext } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { VariableSizeList as List } from 'react-window';
import { desoToDesoNanos, tokenNanosToInteger } from 'utils/currency';
import { cn } from 'utils/shadcn';
import { Skeleton } from 'components/shadcn/ui/skeleton';
import { Spinner } from 'components/core/Spinner';
import { PaginationRequestParams, usePaginationRequest } from '../../hooks/usePaginationRequest';
import capitalize from 'lodash/capitalize';
import upperFirst from 'lodash/upperFirst';
import { tableColumns } from './ProjectHoldersTableColumns';
import { DESO_ZERO_PUBLIC_KEY } from '../../constants/AppConstants';
import { deso } from '../../services';
import { QuoteCurrencyContext } from '../../contexts/QuoteCurrencyContext';
import { DESO_TOKEN_DISPLAY_NAME } from '../../utils/constants';
import { getWrappedAsset, isWrappedAsset } from '../../utils/deso';

export const transformTradeOrderByKey = (key: string) => {
  if (/^(VolumeUsd|PriceUsdPctChange)/.test(key)) {
    return key.replace(/(VolumeUsdPast|PriceUsdPctChange)(\d+[HD])_/, '$1_$2');
  }
  return key.replaceAll('_', '');
};

export const getOrderByKey = <T extends typeof TokenBalanceSummariesOrderBy>(
  columnSorting: SortingState,
  orderByEnum: T,
  defaultValue: T[keyof T],
): T[keyof T] => {
  if (!columnSorting.length) {
    return defaultValue;
  }

  const getOrderByKeyInternal = transformTradeOrderByKey(
    `${upperFirst(columnSorting[0].id)}_${capitalize(columnSorting[0]?.desc ? 'Desc' : 'Asc')}`,
  );

  return orderByEnum[getOrderByKeyInternal as keyof T] || defaultValue;
};

interface WalletCreatorCoinHoldingsProps {
  publicKey: string;
  height?: number;
  className?: string;
}

export type TableParams = {
  id: string;
  column: { columnDef: { cell: Renderable<object> } };
  getContext: () => { row: { id: string }; column: { id: string } };
};

const ITEMS_PER_PAGE = 20;

export const TableItem = <T,>({
  data: { table, tableRows, renderLoader, columnsWidth },
  index,
  style,
}: {
  data: {
    table: Table<TableParams>;
    tableRows: Row<T>[];
    renderLoader: () => React.ReactNode;
    columnsWidth: number[];
  };
  index: number;
  style: CSSProperties;
}) => {
  const rowRef = useRef<null | HTMLDivElement>(null);

  const rowModel = tableRows?.[index];

  if (!rowModel) return null;

  const rows = table.getRowModel()?.rows;
  const row = rows?.[index];

  return (
    <div key={row.id} style={style}>
      <div ref={rowRef} className="flex w-full items-stretch border-b">
        {row.getVisibleCells().map(
          (
            cell: Cell<
              {
                id: string;
                column: { columnDef: { cell: Renderable<object> } };
                getContext: () => {
                  row: { id: string };
                  column: { id: string };
                };
              },
              unknown
            >,
            idx,
          ) => (
            <div
              className="relative flex items-center px-2 py-1"
              key={cell.id}
              style={{ width: `${columnsWidth[idx]}%` }}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </div>
          ),
        )}
      </div>

      {row.id === tableRows[tableRows.length - 1].id && renderLoader()}
    </div>
  );
};

const HoldersTable = ({ publicKey, height = 500, className }: WalletCreatorCoinHoldingsProps) => {
  const { quoteCurrencyPublicKey } = useContext(QuoteCurrencyContext);

  const [holders, setHolders] = useState<Array<TokenBalanceSummary>>([]);

  const [totalCount, setTotalCount] = useState(0);
  const [columnSorting, setColumnSorting] = useState<SortingState>([]);
  const hasMoreHolders = totalCount > holders.length;
  const [holdersPercentageMap, setHoldersPercentageMap] = useState<Record<string, number>>({});
  const [usdValueMap, setUsdValueMap] = useState<Record<string, number>>({});
  const [skipPagination, setSkipPagination] = useState(false);

  const [fetchHolders] = useLazyQuery(HoldersByPublicKeyDocument);
  const [fetchDesoHolders] = useLazyQuery(DesoBalancesDocument);
  const [fetchDesoTotalSupply] = useLazyQuery(TotalSupplyDocument, {
    variables: {
      filter: {
        coinUsername: {
          equalTo: DESO_TOKEN_DISPLAY_NAME,
        },
      },
    },
  });

  const columns = useMemo(
    () =>
      tableColumns({
        holdersPercentageMap,
        usdValueMap,
      }),
    [holdersPercentageMap],
  );

  const getDesoHolders = async (params?: PaginationRequestParams) => {
    const offset = params?.changeParams ? 0 : holders.length;

    const [{ data: desoHolders }, { data: desoTotalSupply }] = await Promise.all([
      fetchDesoHolders({
        variables: {
          first: ITEMS_PER_PAGE,
          offset,
          orderBy:
            columnSorting?.[0]?.id === 'totalBalance' && !columnSorting?.[0]?.desc
              ? DesoBalancesOrderBy.BalanceNanosAsc
              : DesoBalancesOrderBy.BalanceNanosDesc,
        },
      }),
      fetchDesoTotalSupply(),
    ]);

    return {
      data: {
        accountByPublicKey: {
          publicKey: DESO_ZERO_PUBLIC_KEY,
          tokenBalanceSummariesAsCreator: {
            nodes: (desoHolders?.desoBalances?.nodes ?? []).map((holder) => {
              return {
                hodler: holder?.account,
                lockedBalanceNanos: desoToDesoNanos(
                  Number(
                    holder?.account?.accountWealth?.lockedStakeDesoBalanceNanos ??
                      holder?.account?.accountWealthChainUser?.lockedStakeDesoBalanceNanos ??
                      0,
                  ) +
                    Number(
                      holder?.account?.accountWealth?.stakedDesoBalanceNanos ??
                        holder?.account?.accountWealthChainUser?.stakedDesoBalanceNanos ??
                        0,
                    ),
                ),
                totalBalance: desoToDesoNanos(holder?.balanceNanos ?? 0),
              };
            }),
            totalCount: desoHolders?.desoBalances?.totalCount,
          },
          hodlerBalance: {
            nodes: [],
          },
          tokenAggAsCreator: {
            totalBalanceNanos: Number(desoTotalSupply?.daoCoinStats?.nodes?.[0]?.totalSupply ?? '0') * 1e18,
          },
        },
      } as unknown as HoldersByPublicKeyQuery,
    };
  };

  const getHolders = useCallback(
    async (params?: PaginationRequestParams) => {
      if (!publicKey) return;

      const offset = params?.changeParams ? 0 : holders.length;

      const { data } = await (publicKey === DESO_ZERO_PUBLIC_KEY
        ? getDesoHolders(params)
        : fetchHolders({
            fetchPolicy: 'network-only',
            variables: {
              first: ITEMS_PER_PAGE,
              offset,
              publicKey,
              includeTokenCreator: true,
              orderBy: getOrderByKey(
                columnSorting,
                TokenBalanceSummariesOrderBy,
                TokenBalanceSummariesOrderBy.TotalBalanceDesc,
              ),
              filter: {
                hodler: {
                  publicKey: {
                    notEqualTo: publicKey,
                  },
                },
              },
              hodlerBalanceFilter: {
                hodler: {
                  publicKey: {
                    equalTo: publicKey,
                  },
                },
              },
            },
          }));

      const { tokenBalanceSummariesAsCreator, hodlerBalance, tokenAggAsCreator } = data?.accountByPublicKey || {};

      const totalCountSum = (tokenBalanceSummariesAsCreator?.totalCount ?? 0) + (hodlerBalance?.totalCount ?? 0);

      const isWrapped = isWrappedAsset(publicKey, 'publicKey') || publicKey === DESO_ZERO_PUBLIC_KEY;

      // For $DESO and all wrapped assets, we estimate the USD value by simulating a market sell of **1 token**,
      // then multiply that mid-price by the total balance. This avoids slippage-based distortions.

      // For all other tokens, we simulate a full market sell of the user's entire token balance to get a more realistic USD value.
      // This approach accounts for slippage, AMM liquidity depth, and price impact.
      const prices = isWrapped
        ? await deso.getBaseCurrencyPrice([
            {
              BaseCurrencyPublicKeyBase58Check: publicKey ?? '',
              BaseCurrencyQuantityToSell: 1,
              QuoteCurrencyPublicKeyBase58Check: quoteCurrencyPublicKey,
            },
          ])
        : await deso.getBaseCurrencyPrice(
            (tokenBalanceSummariesAsCreator?.nodes || []).map((balanceSummary) => ({
              BaseCurrencyPublicKeyBase58Check: publicKey ?? '',
              BaseCurrencyQuantityToSell: tokenNanosToInteger(Number(balanceSummary?.totalBalance || 0)),
              QuoteCurrencyPublicKeyBase58Check: quoteCurrencyPublicKey,
            })),
          );

      setUsdValueMap(
        (tokenBalanceSummariesAsCreator?.nodes || []).reduce((acc, curr, index) => {
          if (!curr?.hodler?.publicKey) {
            return acc;
          }

          return {
            ...acc,
            [curr.hodler.publicKey]: isWrapped
              ? Number(prices[0].ReceiveAmountInUsd ?? 0) * tokenNanosToInteger(Number(curr?.totalBalance || 0))
              : (Number(prices[index].ReceiveAmountInUsd) ?? 0),
          };
        }, usdValueMap),
      );

      const newHolders = [
        ...(!offset ? (hodlerBalance?.nodes.filter(Boolean) ?? []) : []),
        ...(tokenBalanceSummariesAsCreator?.nodes.filter(Boolean) ?? []),
      ];

      const totalCoinNanos = Number(tokenAggAsCreator?.totalBalanceNanos ?? 0);

      const newHoldersPercentageMap = newHolders.reduce((acc, holder) => {
        const balanceNanos = Number(holder?.totalBalance);
        const percentage = (balanceNanos / totalCoinNanos) * 100;
        const holderPublicKey = holder?.hodler?.publicKey;

        if (!holderPublicKey) return acc;

        return {
          ...acc,
          [holderPublicKey]: percentage,
        };
      }, {});

      const finalMap = { ...holdersPercentageMap, ...newHoldersPercentageMap };

      setHoldersPercentageMap(finalMap as Record<string, number>);
      setTotalCount(totalCountSum);

      setHolders((oldList) =>
        params?.changeParams
          ? (newHolders as TokenBalanceSummary[])
          : ([...oldList, ...newHolders] as TokenBalanceSummary[]),
      );
    },
    [publicKey, fetchHolders, holders.length, columnSorting, holdersPercentageMap],
  );

  const COLUMNS_WIDTH = [7, 47, 23, 23];
  const table = useReactTable({
    data: holders,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setColumnSorting,
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    state: {
      sorting: columnSorting,
    },
  });

  const { ref, loadingMore, initialLoading, setInitialLoading } = usePaginationRequest({
    initialRequest: getHolders,
    fetchRequest: getHolders,
    hasMoreItems: hasMoreHolders,
    skipRequest: skipPagination,
  });

  const run = () => {
    setSkipPagination(true);
    getHolders({ changeParams: true });
    setSkipPagination(false);
  };

  useEffect(() => {
    if (columnSorting.length) {
      run();
    }
  }, [columnSorting]);

  useEffect(() => {
    setUsdValueMap({});
    setInitialLoading(true);

    run();
  }, [quoteCurrencyPublicKey]);

  const tableRows = table.getRowModel().rows;

  return (
    <>
      <div className={cn('w-full text-muted', className)}>
        {initialLoading ? (
          <div className="px-2 pb-2">
            <Skeleton className="mt-4 h-[40px] w-full" />
            <Skeleton className="mt-4 h-[280px] w-full" />
          </div>
        ) : (
          <div className="w-full overflow-auto">
            {table.getHeaderGroups().map((headerGroup) => (
              <div key={headerGroup.id} className="flex w-full min-w-[600px] border-b">
                {headerGroup.headers.map((header, idx) => {
                  return (
                    <div
                      className="flex h-11 items-center px-2 text-left text-xs font-medium text-muted-foreground"
                      key={header.id}
                      style={{ width: `${COLUMNS_WIDTH[idx]}%` }}
                    >
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    </div>
                  );
                })}
              </div>
            ))}
            {tableRows?.length ? (
              <List
                width="100%"
                height={height}
                itemCount={tableRows.length}
                itemSize={(index) => {
                  return (holders[index]?.lockedBalanceNanos ?? 0) > 0 ? 69.5 : 60;
                }}
                itemData={{
                  table: table as unknown as Table<TableParams>,
                  tableRows,
                  columnsWidth: COLUMNS_WIDTH,
                  renderLoader: () =>
                    ((!initialLoading && holders.length !== totalCount) || loadingMore) && (
                      <div key="load-more-row" ref={ref}>
                        <div className="flex items-center justify-center gap-x-2 py-2">
                          <Spinner size={20} />
                          <span>Loading...</span>
                        </div>
                      </div>
                    ),
                }}
                style={{
                  minWidth: 600,
                }}
              >
                {TableItem}
              </List>
            ) : (
              <div className="h-24 text-center flex items-center justify-center">No holders found.</div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default HoldersTable;
